import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo/Seo";
import { Reveal } from "@/components/Reveal/Reveal";
import { Parallax } from "@/components/Motion/Parallax";
import { ElfsightWidget } from "@/components/Elfsight/ElfsightWidget";
import { ReservationCard } from "./ReservationCard";
import { FEATURED } from "@/data/featured";
import { AMENITIES, ATTRACTIONS } from "@/data/content";
import { mediaUrl } from "@/lib/media";
import { lodgingJsonLd, breadcrumbJsonLd } from "@/lib/structuredData";
import styles from "./BookStay.module.css";

const MOSAIC = [
  FEATURED.ceremony,
  FEATURED.pool,
  FEATURED.living,
  FEATURED.masterBedroom,
  FEATURED.gameRoom,
];

const HIGHLIGHTS = [
  {
    title: "Panoramic Fallbrook views",
    body: "Wake up to sweeping views across 30 private acres in the San Diego hills.",
  },
  {
    title: "Built for gatherings",
    body: "Sleeps up to 16 across 4 bedrooms — and the grounds are made for events.",
  },
  {
    title: "Self check-in",
    body: "A door code is provided for easy, contactless arrival at 3 PM.",
  },
];

export function BookStayPage() {
  return (
    <>
      <Seo
        title="Book a Stay"
        description="Book an overnight stay at Villa Rosa — a 30-acre Fallbrook estate sleeping up to 16 guests across 4 bedrooms and 3 baths. Request your dates or book through Airbnb and Vrbo."
        path="/book-a-stay"
        image={mediaUrl(FEATURED.ceremony)}
        jsonLd={[
          lodgingJsonLd(mediaUrl(FEATURED.ceremony)),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Book a Stay", path: "/book-a-stay" },
          ]),
        ]}
      />

      <div className="container">
        {/* Title row */}
        <header className={styles.titleRow}>
          <p className={`eyebrow eyebrow--rule ${styles.eyebrow}`}>
            The stay · Fallbrook, California
          </p>
          <h1 className={styles.title}>Villa Rosa</h1>
          <p className={styles.subtitle}>
            Entire villa · Sleeps 16 · 30 private acres
          </p>
        </header>

        {/* Photo mosaic */}
        <Reveal variant="fade" className={styles.mosaic}>
          {MOSAIC.map((path, i) => (
            <div
              key={path}
              className={`${styles.mosaicItem} ${i === 0 ? styles.mosaicMain : ""}`}
            >
              {i === 0 ? (
                <Parallax
                  speed={0.12}
                  className={styles.mosaicParallax}
                  style={{ backgroundImage: `url(${mediaUrl(path)})` }}
                >
                  <span />
                </Parallax>
              ) : (
                <img
                  src={mediaUrl(path)}
                  alt="Villa Rosa property"
                  loading="lazy"
                  decoding="async"
                />
              )}
            </div>
          ))}
          <Link to="/gallery" className={styles.showAll}>
            <span className={styles.showAllIcon} aria-hidden="true" />
            Show all photos
          </Link>
        </Reveal>

        {/* Main content + reservation card */}
        <div className={styles.layout}>
          <div className={styles.content}>
            <Reveal as="section" className={styles.block}>
              <h2 className={styles.h2}>Entire villa hosted by the Villa Rosa team</h2>
              <p className={styles.facts}>
                16 guests · 4 bedrooms · 3 full baths · 30 acres · 3,900+ sq ft
              </p>
            </Reveal>

            <div className={styles.divider} />

            <section className={styles.block}>
              <ul className={styles.highlights}>
                {HIGHLIGHTS.map((h, i) => (
                  <Reveal as="li" key={h.title} variant="up" delay={i * 80} className={styles.highlight}>
                    <span className={styles.highlightDot} aria-hidden="true" />
                    <div>
                      <strong>{h.title}</strong>
                      <span>{h.body}</span>
                    </div>
                  </Reveal>
                ))}
              </ul>
            </section>

            <div className={styles.divider} />

            <Reveal as="section" className={styles.block}>
              <h3 className={styles.h3}>About this place</h3>
              <p>
                Escape to serenity at our charming retreat in the heart of
                Fallbrook, California. With panoramic views, a spacious layout,
                and beautiful surroundings, Villa Rosa is an idyllic sanctuary
                for those seeking peace and tranquility — and a stunning venue
                for weddings, events, and celebrations.
              </p>
              <p>
                The home comfortably welcomes up to 16 overnight guests. Need an
                extra bed? We offer a fold-up cot, a pull-out pillow-top
                mattress, and two queen air mattresses, with bedding and pillows
                provided.
              </p>
            </Reveal>

            <div className={styles.divider} />

            <Reveal as="section" className={styles.block}>
              <h3 className={styles.h3}>What this place offers</h3>
              <ul className={styles.amenityGrid}>
                {AMENITIES.map((a, i) => (
                  <Reveal as="li" key={a} variant="up" delay={(i % 4) * 50} className={styles.amenityItem}>
                    <span className={styles.amenityCheck} aria-hidden="true">
                      ✓
                    </span>
                    {a}
                  </Reveal>
                ))}
              </ul>
            </Reveal>

            <div className={styles.divider} />

            <Reveal as="section" className={styles.block}>
              <h3 className={styles.h3}>Where you'll be</h3>
              <p className={styles.facts}>Fallbrook, California · North San Diego County</p>
              <ul className={styles.attractions}>
                {ATTRACTIONS.map((a) => (
                  <li key={a.name}>
                    <span>{a.name}</span>
                    <span className={styles.attractionDetail}>{a.detail}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <aside className={styles.aside}>
            <ReservationCard />
          </aside>
        </div>

        <section className={styles.reviews}>
          <div className={styles.divider} />
          <Reveal as="h3" className={styles.h3}>
            Guest reviews
          </Reveal>
          <ElfsightWidget appId="85cd97ed-9ee5-45a4-a3f4-03225ce9756b" />
        </section>
      </div>
    </>
  );
}
